import React, { useCallback, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronsUpDownIcon, CirclePlusIcon, FileTextIcon, LayoutGridIcon, LogOutIcon, SettingsIcon, TruckIcon, UsersIcon, XIcon } from 'lucide-react';
import { AkkoBadge, DistrictLogo } from '../BrandMark';
import { useAuth } from '../../contexts/AuthContext';
import { useOutsideClick } from '../../hooks/useOutsideClick';
const primaryNav = [{
  to: '/overview',
  label: 'Overview',
  icon: LayoutGridIcon
}, {
  to: '/claims',
  label: 'Claims',
  icon: FileTextIcon
}, {
  to: '/shipments',
  label: 'Shipments',
  icon: TruckIcon
}, {
  to: '/student-directory',
  label: 'Student Directory',
  icon: UsersIcon
}];
const quickActions = [{
  to: '/claims/create',
  label: 'File New Claim'
}, {
  to: '/shipments/create',
  label: 'Create New Shipment'
}, {
  to: '/devices/create',
  label: 'Add New Device'
}];
const itemBase = 'flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] leading-tight transition-colors';
export function Sidebar() {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const close = useCallback(() => setMenuOpen(false), []);
  const ref = useOutsideClick<HTMLDivElement>(close);
  return <aside className="fixed inset-y-0 left-0 z-30 flex w-[147px] flex-col bg-navy-900 text-white" data-id="element-82" data-mp="1i97k4s72j3ri">
      <div className="flex flex-col items-center gap-2.5 px-3 pb-3 pt-3.5" data-id="element-83" data-mp="2ezgioa89k9fs">
        <DistrictLogo data-id="element-84" data-mp="29zu705833193" />
        <AkkoBadge data-id="element-85" data-mp="29zbz2bc26ylc" />
      </div>

      <nav className="px-2 pt-3" aria-label="Main navigation" data-id="element-86" data-mp="2a0dest40s99e">
        <ul className="space-y-0.5" data-id="element-87" data-mp="25xqr94edtai0">
          {primaryNav.map(({
          to,
          label,
          icon: Icon
        }) => <li key={to} data-id="element-88" data-mp="20zp0dxaps6vz">
              <NavLink to={to} className={({
            isActive
          }) => `${itemBase} ${isActive ? 'bg-navy-700 font-medium text-white' : 'text-white/85 hover:bg-navy-800 hover:text-white'}`} data-id="element-89" data-mp="20ydwzyl6l15e">
                <Icon className="h-[15px] w-[15px] shrink-0" strokeWidth={1.75} data-id="element-90" data-mp="w08urxlt8wi4" />
                <span data-id="element-91" data-mp="vy65xgwe99ez">{label}</span>
              </NavLink>
            </li>)}
        </ul>
      </nav>

      <p className="px-3.5 pb-1.5 pt-5 text-[11px] text-white/40" data-id="element-92" data-mp="i2u5csvk4oq5">Quick Actions</p>
      <ul className="space-y-0.5 px-2" data-id="element-93" data-mp="m45w51q63fzx">
        {quickActions.map(({
        to,
        label
      }) => <li key={to} data-id="element-94" data-mp="89d79coe0ngo">
            <NavLink to={to} className={({
          isActive
        }) => `${itemBase} ${isActive ? 'bg-navy-700 font-medium text-white' : 'text-white/85 hover:bg-navy-800 hover:text-white'}`} data-id="element-95" data-mp="88v5y1mvbj1w">
              <CirclePlusIcon className="h-[15px] w-[15px] shrink-0" strokeWidth={1.75} data-id="element-96" data-mp="3lgogxtei2v85" />
              <span data-id="element-97" data-mp="3qg198to54ui4">{label}</span>
            </NavLink>
          </li>)}
      </ul>

      <div ref={ref} className="relative mt-auto p-2" data-id="element-98" data-mp="36uhid1huvkg0">
        {menuOpen && <div className="absolute bottom-full left-2 right-2 mb-1 overflow-hidden rounded-md bg-white py-1 shadow-pop" data-id="element-99" data-mp="36wc8qvbwc4rh">
            <button type="button" onClick={() => {
          setMenuOpen(false);
          navigate('/account-settings');
        }} className="flex w-full items-center gap-2 px-3 py-2 text-[12.5px] text-ink-700 hover:bg-ink-50" data-id="element-100" data-mp="3fuxokipzh5ok">
              <SettingsIcon className="h-4 w-4 text-ink-500" strokeWidth={1.75} data-id="element-101" data-mp="3kuk2yvhjsryy" />
              Settings
            </button>
            <button type="button" onClick={() => setMenuOpen(false)} className="flex w-full items-center gap-2 px-3 py-2 text-[12.5px] text-ink-700 hover:bg-ink-50" data-id="element-102" data-mp="3cr2bfgwnwaq">
              <LogOutIcon className="h-4 w-4 text-ink-500" strokeWidth={1.75} data-id="element-103" data-mp="cf0e08nwtvpp" />
              Logout
            </button>
          </div>}
        <button type="button" onClick={() => setMenuOpen(v => !v)} aria-expanded={menuOpen} aria-label={`Account menu for ${user.name}`} className="flex w-full items-center gap-2 rounded-md px-1.5 py-2 text-left hover:bg-navy-800" data-id="element-104" data-mp="21vuf7qkvodeh">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-[11px] font-semibold text-white" data-id="element-105" data-mp="2fmzhl0fmrjsi">
            {user.initials}
          </span>
          <span className="min-w-0 flex-1" data-id="element-106" data-mp="1iz325yvxiuiv">
            <span className="block truncate text-[12px] font-bold leading-tight" data-id="element-107" data-mp="1nwuswpku8alh">{user.name}</span>
            <span className="block text-[11px] leading-tight text-white/55" data-id="element-108" data-mp="1rwdbvft3mblo">{user.role}</span>
          </span>
          {menuOpen ? <XIcon className="h-3.5 w-3.5 shrink-0 text-white/60" data-id="element-109" data-mp="1wxsvr4jjic2h" /> : <ChevronsUpDownIcon className="h-3.5 w-3.5 shrink-0 text-white/60" data-id="element-110" data-mp="33c1yg62fx8ly" />}
        </button>
      </div>
    </aside>;
}
export function SidebarSkeleton() {
  return <aside className="fixed inset-y-0 left-0 z-30 flex w-[147px] flex-col bg-navy-900/10 text-white" data-id="element-111" data-mp="3iea5fr8qq7sw">
      <div className="flex flex-col items-center gap-2.5 px-3 pb-3 pt-3.5" data-id="element-112" data-mp="3dhtgiutpvper">
        <span className="h-[58px] w-full animate-pulse rounded-md bg-ink-200" data-id="element-113" data-mp="38p0e4dlq0t5u" />
        <span className="opacity-40" data-id="element-114" data-mp="34ogft6pevji4">
          <AkkoBadge tone="light" data-id="element-115" data-mp="34nf3z00kmszc" />
        </span>
      </div>
      <div className="px-2 pt-3" data-id="element-116" data-mp="2zou5p64vqjdj">
        <span className={`${itemBase} bg-ink-200/60 text-ink-400`} data-id="element-117" data-mp="2zpwtv6ati7pr">
          <LayoutGridIcon className="h-[15px] w-[15px]" strokeWidth={1.75} data-id="element-118" data-mp="2uphigka3jaaf" />
          Overview
        </span>
      </div>
      <p className="px-3.5 pb-1.5 pt-5 text-[11px] text-ink-300" data-id="element-119" data-mp="2prz6sm4p2gyq">Quick Actions</p>
      <div className="px-2" data-id="element-120" data-mp="2lpucrmac3ibk">
        <span className={`${itemBase} text-ink-400`} data-id="element-121" data-mp="2lqnn0x8i4pyg">
          <CirclePlusIcon className="h-[15px] w-[15px]" strokeWidth={1.75} data-id="element-122" data-mp="nzb0u50o1ff9" />
          File New Claim
        </span>
      </div>
      <div className="mt-auto flex items-center gap-2 p-3.5" data-id="element-123" data-mp="jz0v8tgscr0x">
        <span className="h-7 w-7 animate-pulse rounded-full bg-ink-200" data-id="element-124" data-mp="2dgoqlmhshjr1" />
        <span className="h-3 flex-1 animate-pulse rounded bg-ink-200" data-id="element-125" data-mp="2deloi43d405y" />
      </div>
    </aside>;
}